/**
 * Client-side image compression for the web app.
 *
 * - Resizes to max 1920px on the longest side
 * - Compresses as JPEG, targeting under 500 KB
 * - Progressively lowers quality if still too large
 *
 * NEVER THROWS. On any failure it returns the original file: compression is an
 * optimisation, and a picture the browser can't re-encode must still be
 * sendable. (Rejecting here used to kill the whole send with an uncaught
 * "Canvas toBlob failed".)
 */

const MAX_DIMENSION = 1920;
const MAX_BYTES = 500 * 1024; // 500 KB
const QUALITY_STEPS = [0.8, 0.6, 0.5, 0.4, 0.3];

/**
 * Decode a file to something drawable. `createImageBitmap` is preferred: it
 * reports real intrinsic dimensions, where `new Image()` can report 0x0 (an SVG
 * with no width/height, say) — and a 0x0 canvas makes toBlob return null, which
 * is exactly how this blew up.
 */
async function decode(file: File): Promise<ImageBitmap | HTMLImageElement | null> {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file);
    } catch {
      // Fall through to the <img> path (older browsers, odd codecs).
    }
  }
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => resolve(img);
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

function toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  // Resolve, never reject: a null blob is a normal outcome the caller handles.
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
}

/**
 * Compress an image File. Returns a new JPEG File under MAX_BYTES when it can,
 * and the original file otherwise.
 * @param maxDimension Override the default max dimension (1920px). Use 512 for profile pictures.
 */
export async function compressImage(file: File, maxDimension = MAX_DIMENSION): Promise<File> {
  try {
    if (!file || !file.type || !file.type.startsWith("image/")) return file;
    // GIFs would lose their animation, SVGs are vectors and have no business
    // being rasterised here.
    if (file.type === "image/gif" || file.type === "image/svg+xml") return file;
    // Small enough already — skip.
    if (file.size <= MAX_BYTES) return file;

    const source = await decode(file);
    if (!source) return file;

    let width = source.width;
    let height = source.height;
    // A zero-sized source yields a zero-sized canvas, and toBlob returns null.
    if (!width || !height) return file;

    if (Math.max(width, height) > maxDimension) {
      if (width >= height) {
        height = Math.round((height * maxDimension) / width);
        width = maxDimension;
      } else {
        width = Math.round((width * maxDimension) / height);
        height = maxDimension;
      }
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(source as CanvasImageSource, 0, 0, width, height);

    if ("close" in source && typeof source.close === "function") source.close();
    else if (source instanceof HTMLImageElement) URL.revokeObjectURL(source.src);

    const name = file.name.replace(/\.[^/.]+$/, "") + ".jpg";

    for (const quality of QUALITY_STEPS) {
      const blob = await toBlob(canvas, quality);
      // The browser refused to encode at all — nothing better to try.
      if (!blob) return file;
      if (blob.size <= MAX_BYTES) {
        return new File([blob], name, { type: "image/jpeg", lastModified: Date.now() });
      }
    }

    // Still over target at the lowest quality: keep the smallest we produced,
    // but never hand back something bigger than what we started with.
    const last = await toBlob(canvas, QUALITY_STEPS[QUALITY_STEPS.length - 1]);
    if (!last || last.size >= file.size) return file;
    return new File([last], name, { type: "image/jpeg", lastModified: Date.now() });
  } catch (error) {
    console.warn("Compression d'image impossible, envoi du fichier original:", error);
    return file;
  }
}

/**
 * Compress an array of Files in parallel. compressImage never rejects, so one
 * awkward picture can no longer take the whole batch (and the send) down with it.
 */
export async function compressImages(files: File[]): Promise<File[]> {
  return Promise.all(files.map((f) => compressImage(f)));
}
